import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Icon from '../../common/Icons';
import { PHARMACY_COPY } from '../pharmacyCopy';
import { searchMedications } from './medicationSearch';
import { HighlightedText } from './smartPickerControls';

const copy = PHARMACY_COPY.INVENTORY;

function medicationMeta(med) {
  return [med?.category_name, med?.unit_name, med?.supplier].filter(Boolean).join(' · ');
}

export default function MedicationSmartPicker({
  medications = [],
  value,
  onChange,
  onPicked,
  autoFocus = false,
  disabled = false
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(true);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => medications.find((med) => String(med.id) === String(value)) || null,
    [medications, value]
  );

  const results = useMemo(
    () => searchMedications(medications, query, { limit: query.trim() ? 12 : 8 }),
    [medications, query]
  );

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    if (!autoFocus || selected) return undefined;
    const t = window.setTimeout(() => searchRef.current?.focus(), 40);
    return () => window.clearTimeout(t);
  }, [autoFocus, selected]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const pick = (med) => {
    onChange(String(med.id));
    setQuery(med.name || '');
    setOpen(false);
    if (typeof onPicked === 'function') onPicked(med);
  };

  const clearSelection = () => {
    onChange('');
    setQuery(selected?.name || '');
    setOpen(true);
    window.setTimeout(() => searchRef.current?.focus(), 0);
  };

  const onQueryChange = (next) => {
    setQuery(next);
    setOpen(true);
    if (selected && next.trim() !== String(selected.name || '').trim()) onChange('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter' && open && results[active]) {
      e.preventDefault();
      pick(results[active]);
      return;
    }
    if (e.key === 'Escape') setOpen(false);
  };

  const activeId = open && results[active] ? `inv-med-opt-${results[active].id}` : undefined;
  const emptyQuery = !query.trim();

  if (selected) {
    return (
      <div className="cp-inv-med-picker">
        <Form.Label className="cp-inv-modal-label">{copy.FIELD_MED}</Form.Label>
        <div className="cp-inv-med-selected">
          <span className="cp-inv-med-selected__icon" aria-hidden>
            <Icon name="pills" size={18} />
          </span>
          <div className="cp-inv-med-selected__main">
            <strong>{selected.name}</strong>
            {medicationMeta(selected) ? <span>{medicationMeta(selected)}</span> : null}
          </div>
          <button
            type="button"
            className="cp-modal-btn cp-modal-btn--ghost"
            onClick={clearSelection}
            disabled={disabled}
          >
            {copy.FIELD_MED_CHANGE}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-inv-med-picker" ref={rootRef}>
      <Form.Label className="cp-inv-modal-label" htmlFor="inv-med-search">
        {copy.FIELD_MED}
      </Form.Label>
      <div className="cp-smart-picker cp-inv-med-picker__box">
        <div className="cp-smart-picker__control">
          <Icon name="search" size={16} className="cp-smart-picker__icon" aria-hidden />
          <input
            id="inv-med-search"
            ref={searchRef}
            type="search"
            className="cp-smart-picker__input"
            placeholder={copy.FIELD_MED_SEARCH}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            disabled={disabled}
            role="combobox"
            aria-expanded={open}
            aria-controls="inv-med-list"
            aria-activedescendant={activeId}
            aria-autocomplete="list"
          />
        </div>
        {open ? (
          <ul className="cp-smart-picker__list" id="inv-med-list" role="listbox" ref={listRef}>
            {results.length === 0 ? (
              <li className="cp-smart-picker__empty">
                {emptyQuery ? copy.FIELD_MED_EMPTY : copy.FIELD_MED_NO_MATCH}
              </li>
            ) : (
              results.map((med, idx) => {
                const isActive = idx === active;
                return (
                  <li key={med.id}>
                    <button
                      type="button"
                      id={`inv-med-opt-${med.id}`}
                      role="option"
                      aria-selected={isActive}
                      data-active={isActive ? 'true' : undefined}
                      className={`cp-smart-picker__option${isActive ? ' is-active' : ''}`}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => pick(med)}
                    >
                      <span className="cp-smart-picker__option-main">
                        <strong>
                          <HighlightedText text={med.name} query={query} />
                        </strong>
                        <span className="cp-smart-picker__option-meta">
                          {medicationMeta(med) || copy.FIELD_MED_CHOOSE}
                        </span>
                      </span>
                      <span className="cp-smart-picker__add">{copy.FIELD_MED_CHOOSE}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        ) : null}
      </div>
      <p className="cp-inv-med-hint">{copy.FIELD_MED_HINT}</p>
    </div>
  );
}
